import React, { useState, useEffect } from "react";
import { getPath } from "../../../utils";
import BlankState from "../../Common/BlankState";
import styled from "styled-components";
import variables from "../../../constants/assets/variables";
import ListPagination from "../../Common/ListPagination";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as FeatureConfigActions from "../../../store/actions/FeatureConfig";

const SAFE_WIDTH = 100;

export const defaultFieldValue = (row, key) => {
  const value = getPath(row, key, "");

  if (typeof value !== "number" && typeof value !== "string") {
    return JSON.stringify(value);
  }

  return value;
};

const ConfigTable = (props) => {
  const {
    fields,
    list,
    getFieldValue = defaultFieldValue,
    fontSize,
    rowClickable,
    onRowClick,
    dataTestid,
    isAdditionalRows = false,
    getAdditionalRows,
    wrapContents = false,
    prevPage,
    nextPage,
    selectedNamespace,
    selectedKey,
    pageTokens,
    handlePagination,
    blankStateComponent = <BlankState />,
    activeHierarchyFiltersConfig,
    currentPage,
    setCurrentPage,
  } = props;

  const handleNavClick = (action) => {
    let page = currentPage;
    if (action === "prev") {
      page = currentPage - 1;
    } else {
      page = currentPage + 1;
    }
    setCurrentPage(page);
    const payload = {
      filters: {
        key: selectedKey,
      },
      namespace: selectedNamespace,
      pagination: {
        limit: 20,
        pageToken: pageTokens[currentPage]?.pageToken,
      },
      page: page,
    };

    if (activeHierarchyFiltersConfig.entity != undefined) {
      payload.filters.hierarchy = activeHierarchyFiltersConfig;
    }

    if (selectedKey == "" && activeHierarchyFiltersConfig.entity == undefined) { 
      payload.filters = {
        hierarchy: {
          entity: "",
          entityId: "",
        },
      };
      
    }

    props.filterKeyConfigs(payload).then((res) => {
      handlePagination(page, { pageToken: res.pagination?.pageToken });
      handlePagination(page, { pageToken: res.pagination?.nextPageToken });
    });
  };

  const handleRowClick = (item) => {
    if (rowClickable) {
      onRowClick(item);
    }
  };

  const getFieldWidth = (fields) => {
    const outerTable = document.getElementById("tableContainer");
    if (outerTable) {
      const oneFieldWidth = Math.min(parseInt(outerTable.clientWidth) / fields.length, SAFE_WIDTH);
      return oneFieldWidth;
    }
    return SAFE_WIDTH;
  };

  return (
    <TableContainer
      data-testid={dataTestid}
      fontSize={fontSize}
      wrapContents={wrapContents}
      id="tableContainer"
      className="table-container h-full"
    >
      {getPath(list, "length", undefined) > 0 ? (
        <table border="1" className="list-table">
          <tbody>
            <tr className="tableHeadRow">
              {fields.map((field, index) => {
                return (
                  <th key={field.heading + index} className="header">
                    {field.heading}
                  </th>
                );
              })}
            </tr>
            {list.map((data, index) => (
              <tr
                onClick={() => handleRowClick(data)}
                className={`tableBodyRow ${rowClickable && "cursor-pointer"}`}
                key={data.id || index}
                data-testid={`row${index}`}
              >
                {fields.map((field, idx) => {
                  return (
                    <td
                      id={field.heading + index + idx}
                      data-testid={field.heading + index + idx}
                      key={field.heading + index + idx}
                      component="th"
                      className={
                        wrapContents
                          ? "bigVal"
                          : `${
                              parseInt(
                                (getFieldValue(data, field.key, field) + "")
                                  .length
                              ) > getFieldWidth(fields) && "bigVal"
                            }`
                      }
                    >
                      {getFieldValue(data, field.key, index, data)}
                    </td>
                  );
                })}
              </tr>
            ))}
            {isAdditionalRows && getAdditionalRows()}
          </tbody>
        </table>
      ) : (
        <div>{blankStateComponent}</div>
      )}
      <ListPagination
        nextPage={nextPage}
        prevPage={prevPage}
        handleNavClick={handleNavClick}
      />
    </TableContainer>
  );
};

const TableContainer = styled.section`
  background: ${variables.white};
  border: 0.5px solid ${variables.grey300};
  box-sizing: border-box;
  border-radius: 8px;
  height: fit-content;
  max-height: 100%;
  width: 80%;
  margin: auto;
  overflow-y: scroll;

  .list-table {
    width: 100%;
    border-collapse: collapse;
    border: 0.5px solid ${variables.grey100};
    font-size: ${({ fontSize }) => (fontSize ? fontSize : "0.875rem")};
    .tableBodyRow {
      font-weight: 500;
      color: ${variables.grey900};
    }
    th {
      line-height: 1rem;
      text-align: left;
      font-weight: normal;
      color: ${variables.grey600};
    }
    td,
    th {
      padding: 18px 12px;
      max-width: 300px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: normal;
    }
    .bigVal {
      word-break: break-word;
      white-space: pre-line;
    }
    .tableBodyRow:nth-child(even) {
      background-color: ${variables.grey100};
    }
  }
`;

function mapStateToProps(state) {
  const { FeatureConfig } = state;
  return {
    activeHierarchyFiltersConfig: FeatureConfig.activeHierarchyFiltersConfig,
    prevPage: FeatureConfig.prevPage,
    nextPage: FeatureConfig.nextPage,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    filterKeyConfigs: bindActionCreators(
      FeatureConfigActions.filterKeyConfigs,
      dispatch
    ),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(React.memo(ConfigTable));
